import moment from 'moment';
import {ActivityType} from '../../../../../../backend/interfaces/activity-type';
import {CarePlanConfigurationFeature} from '../../../../../../backend/enums/care-plan-configuration-feature';
import {PosologyScheduleFormType} from '../../../../../../types/schedule';
import {scheduleReadableExpression} from '../../../../../functions/schedule-readable-expression';
import {ExportCarePlanTableColumnStyle} from '../../base/export-care-plan-types';
import {
  RowData,
  ExportCarePlanGroupDataProcessor,
} from '../../base/export-care-plan-group-data-processor';
import {translations} from '../../../../../../i18n/translations';
import {PosologySchedule} from '../../../../../../backend/enums/posology-schedule';
import {safeParseJson} from '../../../../../functions/safe-parse-json';
import {abbreviateMealName, getMealsActivityTypes} from './export-care-plan-medication-utils';

export class MedicationPdfDataProcessor extends ExportCarePlanGroupDataProcessor {
  private mealActivityTypes: ActivityType[] = [];

  override async resolveTaskHeaders(firstRowData: RowData): Promise<string[]> {
    this.mealActivityTypes = await getMealsActivityTypes(this.baseData.entity.id);

    // 1. start-date
    const startHeader = this.t(translations.export.start_date);

    // 2. end-date (from schedule.endDate)
    const endHeader = this.t(translations.export.end_date);

    // 3. medicine name + dosage + unit + administration route
    const detailsHeader = this.t(translations.export.details);

    // 4. one column per meal activity type (periods of the day)
    const mealHeaders = this.mealActivityTypes.map((m) => abbreviateMealName(m.name));

    // 5. schedule readable expression (non-PeriodsOfTheDay types only)
    const scheduleHeader = this.t(translations.export.others);

    // 6. notes
    const notesHeader = this.t(translations.export.observations);

    return [startHeader, endHeader, detailsHeader, ...mealHeaders, scheduleHeader, notesHeader];
  }

  override resolveTaskRow(rowData: RowData) {
    const result: string[] = [];

    // 1. start-date
    const startDate = rowData.getEntryByFeature(CarePlanConfigurationFeature.StartsAtTime)?.value;
    result.push(startDate ? moment(startDate).format('DD/MM/YYYY') : '');

    // 2. end-date (from schedule.endDate)
    const schedule: PosologyScheduleFormType | undefined = safeParseJson(
      rowData.getEntryByFeature(CarePlanConfigurationFeature.Schedule)?.value
    );
    result.push(schedule?.endDate ? moment(schedule.endDate).format('DD/MM/YYYY') : '');

    // 3. medicine name + dosage + unit + administration route
    const medicine = rowData.getEntryByFeature(CarePlanConfigurationFeature.Medicine)?.value;
    const taskName = rowData.getEntryByFeature(CarePlanConfigurationFeature.Name)?.value;
    const dosage = rowData.getEntryByFeature(CarePlanConfigurationFeature.MedicineDosage)?.value;
    const unit = rowData.getEntryByFeature(CarePlanConfigurationFeature.MedicineDosageUnit)?.value;
    const adminRoute = rowData.getEntryByFeature(
      CarePlanConfigurationFeature.MedicineAdministrationRoute
    )?.value;

    const taskNameText = `<b>${taskName}:</b>`;
    const medicineText = `<b>${medicine}</b>`;
    let taskNameAndMedicineText = `${taskNameText}`;
    if (medicine) taskNameAndMedicineText += `\n${medicineText}`;
    let dosageText = dosage ?? '';
    dosageText = unit ? `${dosageText} ${unit}`.trim() : dosageText;
    const adminRouteText = adminRoute && (dosage || unit) ? `, ${adminRoute}` : (adminRoute ?? '');
    if (dosageText !== '' || adminRouteText !== '') dosageText = `\n${dosageText}`;
    const combinedValue = `${taskNameAndMedicineText}${dosageText}${adminRouteText}`;
    result.push(combinedValue);

    // 4. one column per meal activity type (periods of the day)
    for (const mealActivityType of this.mealActivityTypes) {
      if (schedule?.posologyScheduleType !== PosologySchedule.PeriodsOfTheDay) {
        result.push('');
      } else {
        const period = schedule.periodsOfTheDay?.find(
          (p) => p.activityTypeId === mealActivityType.id
        );
        result.push(period ? String(period.quantity) : '');
      }
    }

    // 5. schedule readable expression (non-PeriodsOfTheDay types only)
    if (schedule?.posologyScheduleType === PosologySchedule.PeriodsOfTheDay) {
      result.push('');
    } else {
      const dateExpression = schedule
        ? scheduleReadableExpression({t: this.t, scheduleDefinition: schedule})
        : undefined;
      result.push(dateExpression ?? this.t(translations.export.no_repetition));
    }

    // 6. notes
    const notes = rowData.getEntryByFeature(CarePlanConfigurationFeature.Notes)?.value;
    result.push((notes as string) ?? '');

    return result;
  }

  override resolveTasksColumnStyles(
    taskHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    return {
      // start-date
      0: {cellWidth: 20},
      // end-date
      1: {cellWidth: 20},
      // medicine name + dosage + unit + administration route
      2: {cellWidth: 60},
      // ---
      // Meals columns(Jejum, Almoço, Jantar..)
      // Note: These are dynamic. We don't know the length.
      // ---
      // others (schedule readable expression)
      [taskHeaders.length - 2]: {cellWidth: 50},
      // notes
      [taskHeaders.length - 1]: {cellWidth: 50},
    };
  }

  override resolveDetailsColumnStyles(
    detailsHeaders: string[]
  ): ExportCarePlanTableColumnStyle | undefined {
    return {
      0: {cellWidth: 120},
    };
  }
}
